import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@tempmee/react';

/**
 * The shell both member-content dialogs share: a titled dialog whose body is
 * one admin read of one member's submission (GOTU-7392).
 *
 * It exists because the application and the feedback dialog have the same four
 * outcomes and must not drift apart on any of them — in particular on the two
 * that are absences rather than failures. Reading nothing is the normal answer
 * for a member who never applied and for one nobody has reviewed, and the
 * distinction between those and a 403/500 is the whole point of the screen.
 *
 * The four outcomes are resolved to one status here rather than left as a chain
 * of `!isPending && !isError && …` guards at each call site: those re-derive the
 * same conditions per branch, and getting one of them wrong renders two states
 * at once. Exactly one branch can render.
 */
const statusOf = ({ isPending, isError, isEmpty }) => {
  if (isPending) return 'loading';
  if (isError) return 'error';
  if (isEmpty) return 'empty';

  return 'ready';
};

export const MemberContentDialog = ({
  open,
  onClose,
  title,
  isPending,
  isError,
  isEmpty,
  loadingMessage,
  errorMessage,
  emptyMessage,
  children,
}) => {
  const status = statusOf({ isPending, isError, isEmpty });

  return (
    <Dialog open={open} onClose={onClose} size="large">
      <DialogTitle closeLabel="Dismiss dialog">{title}</DialogTitle>

      <DialogContent>
        {status === 'loading' ? (
          <Typography className="text-text-secondary">{loadingMessage}</Typography>
        ) : null}

        {status === 'error' ? (
          <Typography className="text-error">{errorMessage}</Typography>
        ) : null}

        {status === 'empty' ? (
          <Typography className="text-text-secondary">{emptyMessage}</Typography>
        ) : null}

        {status === 'ready' ? children : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The roster row a member-content dialog was opened from. Shared so the two
 * dialogs cannot disagree about what they are handed.
 */
export const memberShape = PropTypes.shape({
  userId: PropTypes.string.isRequired,
  /** Already dashed when the identities mirror has yet to name them. */
  displayName: PropTypes.string.isRequired,
  /** The raw name, which may be empty — see MemberAnswerList's applicantName. */
  name: PropTypes.string,
});

MemberContentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /**
   * The read succeeded and returned nothing. An absence, not a failure — it
   * takes the muted empty copy rather than the error colour.
   */
  isEmpty: PropTypes.bool.isRequired,
  loadingMessage: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  /** The loaded content. Rendered only once the read has settled with something. */
  children: PropTypes.node,
};

MemberContentDialog.defaultProps = {
  children: null,
};
